import React from 'react';
import { Badge, Button } from '@/components/ui';
import { useOrgaConfigurationContext } from '@/app/providers';

interface PaypalExtensionConfigProps {
    organizationId: string;
    paypalEnabled: boolean;
    onChange: (config: { paypalEnabled: boolean }) => void;
    id?: string; // Suffixe unique pour les IDs
}

const PaypalExtensionConfig: React.FC<PaypalExtensionConfigProps> = ({
    paypalEnabled,
    onChange,
    id = 'default',
}) => {
    const { configuration: fullOrgConfig } = useOrgaConfigurationContext();
    const isPaypalConfigured = !!fullOrgConfig?.paypalConfig?.clientId;

    // État local pour éviter que le switch ne "saute" pendant la sauvegarde
    const [localValue, setLocalValue] = React.useState(paypalEnabled);

    // Synchronisation avec les props
    React.useEffect(() => {
        setLocalValue(paypalEnabled);
    }, [paypalEnabled]);

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        setLocalValue(newValue);
        onChange({ paypalEnabled: newValue });
    };

    return (
        <div className="pt-4 mt-4 border-t border-border/50 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">Options additionnelles</h3>
                    <Badge variant="neutral" size="sm">Bêta</Badge>
                </div>
            </div>

            <div className="space-y-3">
                <div className="flex items-start justify-between gap-4 p-3 rounded-xl bg-background-tertiary border border-border/50">
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <label
                                htmlFor={`paypal-enabled-ext-${id}`}
                                className="text-sm font-medium text-foreground cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                Activer PayPal
                            </label>
                            {!isPaypalConfigured && localValue && (
                                <span className="flex items-center gap-1 text-[10px] text-warning bg-warning/10 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                    Non configuré
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-foreground-secondary mt-1">
                            Permet aux clients de payer via PayPal en plus de la carte.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {!isPaypalConfigured && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.hash = 'payment-paypal';
                                }}
                                className="h-8 text-[11px] px-2"
                            >
                                Configurer PayPal
                            </Button>
                        )}
                        <label
                            className="relative inline-flex items-center cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                id={`paypal-enabled-ext-${id}`}
                                type="checkbox"
                                checked={localValue}
                                onChange={handleToggle}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PaypalExtensionConfig;
